import {
  Button,
  ChevronDownIcon,
  Flex,
  Popover,
  ScrollArea,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Command } from "cmdk";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { updateVisibleSchedules } from "@/entities/planogram";
import { Gondola, useGondolasQuery } from "@/shared/api";
import { useAppDispatch, useAppStore } from "@/app/appStore";
import { planogramInfoTunnel } from "./planogram-tunnel";
import { useSchedules } from "./useSchedules";
import { getStoreImageSrc } from "./helpers/getImageSrc";
import { useCopyToClipboard } from "react-use";
import toast from "react-hot-toast";

type GondolaSelectProps = {
  value: Gondola | undefined | null;
  onValueChange?: (value: Gondola | null) => void;
};

const usePlanogramSchedules = () => {
  const dispatch = useAppDispatch();
  const { activeSchedule, schedules } = useSchedules({
    queryKey: "planogramSchedules",
  });

  useEffect(() => {
    dispatch(updateVisibleSchedules(schedules || []));
  }, [schedules, dispatch]);

  return { activeSchedule };
};

export function GondolaSelect({ value, onValueChange }: GondolaSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { activeSchedule } = usePlanogramSchedules();

  const { data } = useGondolasQuery({
    search,
    on_datetime: activeSchedule?.from_datetime,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const store = useAppStore();

  useHotkeys("ctrl+f", () => setOpen(true), { preventDefault: true });

  useEffect(() => {
    const state = store.getState();

    if (data && value && value.name === state.planogram.selectedGondola?.name) {
      const gondola = data.results.find(
        (gondola) => String(gondola.name) === String(value.name)
      );

      if (gondola) {
        onValueChange?.(gondola);
      }
    }

    if (data && !value) {
      const gondolaQuery = searchParams.get("gondola");

      const gondola = data.results.find(
        (gondola) => String(gondola.name) === String(gondolaQuery)
      );

      if (gondola) {
        onValueChange?.(gondola);
      } else {
        onValueChange?.(data.results[0]);
      }
    }
  }, [data, value, store]);

  const handleSelect = (gondola: Gondola) => () => {
    onValueChange?.(gondola);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("gondola", gondola.name);
      return newParams;
    });
    setOpen(false);
    setSearch("");
  };

  const [_, copy] = useCopyToClipboard();

  const onCopy = (text?: string | number) => {
    if (text) {
      copy(String(text));
      toast.success("Copied to clipboard");
    }
  };

  return (
    <>
      <planogramInfoTunnel.In>
        <Flex gap="3" mr="5">
          <Text size="1" color="gray">
            Planogram ID:{" "}
            <Button
              variant="ghost"
              size="1"
              color="gray"
              onClick={() => onCopy(activeSchedule?.id)}
              className="px-1 py-0.5 -my-0.5 mx-0"
            >
              {activeSchedule?.id || "-"}
            </Button>
          </Text>
          <Text size="1" color="gray">
            Created at:
            <Button
              variant="ghost"
              size="1"
              color="gray"
              onClick={() => onCopy(activeSchedule?.from_datetime)}
              className="px-1 py-0.5 -my-0.5 mx-0"
            >
              {activeSchedule?.from_datetime || "-"}
            </Button>
          </Text>
        </Flex>
      </planogramInfoTunnel.In>

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <Button
            variant="outline"
            size="1"
            color="gray"
            className="min-w-32 justify-between"
          >
            {value ? value.name : "Select gondola"}
            <ChevronDownIcon />
          </Button>
        </Popover.Trigger>
        <Popover.Content width="250px" className="p-0" align="center">
          <Command shouldFilter={false}>
            <Command.Input asChild>
              <TextField.Root
                value={search}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearch(e.target.value);
                }}
                size="1"
                m="2"
                placeholder="Search"
              ></TextField.Root>
            </Command.Input>

            <ScrollArea className="max-h-[400px] pl-2 pr-3 pb-2">
              <Command.List className="space-y-2">
                {data?.results.length === 0 ? (
                  <Command.Empty>No results found.</Command.Empty>
                ) : (
                  data?.results?.map((gondola) => (
                    <Command.Item
                      key={gondola.id}
                      value={String(gondola.id)}
                      onSelect={handleSelect(gondola)}
                      asChild
                    >
                      <Flex
                        gap="2"
                        className={twMerge([
                          "mb-1 last:mb-0 outline-none hover:bg-[var(--gray-a2)] rounded-lg data-[selected='true']:bg-[var(--gray-a3)]",
                          value?.id === gondola.id &&
                            "bg-[var(--indigo-4)] data-[selected='true']:bg-[var(--indigo-4)]",
                        ])}
                      >
                        <figure className="size-16 rounded bg-[var(--gray-a1)]">
                          <img
                            src={getStoreImageSrc(
                              gondola.shelfTrackingCameras?.[0]?.thumbnail
                            )}
                            className="object-contain w-full h-full"
                          />
                        </figure>
                        <Flex direction="column" justify="center">
                          <Text size="2">{gondola.name}</Text>
                        </Flex>
                      </Flex>
                    </Command.Item>
                  ))
                )}
              </Command.List>
            </ScrollArea>
          </Command>
        </Popover.Content>
      </Popover.Root>
    </>
  );
}
